/* cvn-show-more.js — "Show more" under the collection product grid (snippets/cvn-show-more.liquid,
   assets/cvn-show-more.css; linked from sections/main-collection-product-grid). One click adds the
   next batch of products under the ones already showing, in place: no reload, no brand loop, no jump
   to the top. Phone and desktop alike, button only — never automatic, so the section under the grid
   ("Prefer to see each design up close?") is always reachable (Zeus).

   HOW A BATCH ARRIVES. The button is a real link to the next page. A click asks Shopify's Section
   Rendering API for TWO sections of that page, the product grid and the card-colours data
   (`?sections=<grid>,<card-colors>`, ~0.3-0.5s, ~200 KB, against ~900 KB and 1-3s for the whole page
   the old numbered pager made the shopper download). The new cards are appended to #product-grid,
   their colour/size chip data to the card-colours wrap (cvn-card-colors.js builds chips for any new
   card that has data, through its own MutationObserver), and the count, bar and button are updated
   from the fetched control. The link the server wrote is used as-is, so a batch always continues the
   exact result set already on screen — including the drawer's augmented size filter.

   ⚠ THE SLIDE-IN. Dawn reveals grid rows as they scroll into view, but its observer only ever saw the
   cards that were on the page at load: a card added later keeps `scroll-trigger` and would either play
   at once, offscreen, or stay at opacity .01 forever. New cards are therefore put in the offscreen
   state BEFORE they are inserted and handed to a fresh IntersectionObserver running Dawn's OWN
   `onIntersection` (assets/animations.js, global) with Dawn's own rootMargin, so they slide up exactly
   like the first rows. Never re-run `initializeScrollAnimationTrigger` on the grid: it would re-observe
   the rows already shown and fade them out again as they leave the screen. Without Dawn's function
   (reveal setting off) the classes are dropped and the cards simply show.

   ⚠ COMING BACK. Opening a product and pressing Back must land on the same card with every batch
   still there. Each history entry carries `cvnMore` = { id, k (path+search), hrefs (batches loaded),
   a + o (the card at the top of the screen and its offset), y }. The anchor is a CARD, not a scroll
   offset, because chips and lazy rows change heights after a swap and a raw offset lands rows away.
   The card they OPENED is the anchor when the click came from a card; otherwise the first card on
   screen. It is saved when scrolling settles and in the window CAPTURE phase of a click — before
   cvn-nav-feedback.js's document-capture handler empties #MainContent. On `cvn:navigated` (the theme's
   soft back) the batches come back synchronously from memory, so the restored page paints once, at the
   right card; after a hard load (reload, or a back the browser did not cache) they are re-fetched in
   parallel. The memory is keyed by the ENTRY id, not the URL: clearing every filter lands on the
   original URL in a NEW entry, which must start from the first batch.
   ⚠ The drawer's own popstate handler re-renders the grid's first batch whenever it resolves, which can
   be just after the soft back has restored ours; it announces itself with `cvn:grid-updated`, and the
   batches are put back in the same task, so nothing flickers.

   EVENTS: dispatches `cvn:grid-appended` after every append or restore — cvn-filter.js listens and
   re-applies the active colour/size filter to the new cards' chips. */
(function () {
  if (window.cvnMoreBound) return;
  window.cvnMoreBound = true;

  var PH_AFTER = 180;     // ms before the placeholder row appears; a quick answer never shows it
  var MAX_BATCHES = 12;   // batches rebuilt after a hard load
  var mem = {};           // entry id -> { k, items:[html], cc:[html], hrefs:[url], block:html }
  var memIds = [];
  var busy = false;
  var userMoved = false;

  function gridKey() { return location.pathname + location.search; }
  function grid() { return document.getElementById('product-grid'); }
  function moreBlock() { var g = grid(); var c = g && g.parentNode; return c ? c.querySelector('[data-cvn-more]') : null; }
  function sectionIdOf(el) { var s = el && el.closest ? el.closest('.shopify-section') : null; return s ? s.id.replace(/^shopify-section-/, '') : ''; }
  function ccWrap() { return document.querySelector('[id^="shopify-section-template--"] .cvn-cc-data-wrap'); }
  function pathOf(href) {
    try {
      var p = new URL(href, location.href).pathname, i = p.indexOf('/products/');
      return i < 0 ? p : '/products/' + p.slice(i + 10).split('/')[0];
    } catch (e) { return href || ''; }
  }
  function cardPath(li) {
    var a = li.querySelector && li.querySelector('a.full-unstyled-link, a[href*="/products/"]');
    return a ? pathOf(a.getAttribute('href')) : '';
  }
  function cleanHref(href) {
    try {
      var u = new URL(href, location.href);
      u.searchParams.delete('sections'); u.searchParams.delete('section_id');
      return u.pathname + u.search;
    } catch (e) { return href; }
  }
  function sectionsUrl(href, ids) {
    var u = new URL(cleanHref(href), location.href);
    u.searchParams.set('sections', ids.join(','));
    return u.pathname + u.search;
  }
  function ids(g) {
    var gid = sectionIdOf(g), cid = sectionIdOf(ccWrap()), out = [gid];
    if (cid && cid !== gid) out.push(cid);
    return { gid: gid, cid: cid, list: out };
  }
  function nodeFrom(html) { var t = document.createElement('template'); t.innerHTML = html; return t.content.firstElementChild; }
  function fire() { try { document.dispatchEvent(new CustomEvent('cvn:grid-appended')); } catch (e) {} }
  function scrollToY(y) { try { window.scrollTo({ top: y, behavior: 'instant' }); } catch (e) { window.scrollTo(0, y); } }

  /* ---- history entry ---- */
  function entry() {
    var st = history.state;
    var cm = st && typeof st === 'object' ? st.cvnMore : null;
    return cm && cm.k === gridKey() ? cm : null;
  }
  function writeEntry(patch) {
    try {
      var st = history.state && typeof history.state === 'object' ? history.state : {};
      var cm = entry() || { id: 'm' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6), k: gridKey(), hrefs: [] };
      var next = {}, k;
      for (k in cm) next[k] = cm[k];
      for (k in patch) next[k] = patch[k];
      var ns = {};
      for (k in st) ns[k] = st[k];
      ns.cvnMore = next;
      history.replaceState(ns, '');
      return next;
    } catch (e) { return null; }
  }
  function memFor(id) {
    if (!mem[id]) {
      mem[id] = { k: gridKey(), items: [], cc: [], hrefs: [], block: '' };
      memIds.push(id);
      if (memIds.length > 6) delete mem[memIds.shift()];
    }
    return mem[id];
  }

  /* ---- where the shopper is ---- */
  function anchorNow(card) {
    var g = grid(); if (!g) return null;
    if (card && card.parentNode === g) {
      var cp = cardPath(card);
      if (cp) return { a: cp, o: Math.round(card.getBoundingClientRect().top) };
    }
    var kids = g.children;
    for (var i = 0; i < kids.length; i++) {
      var r = kids[i].getBoundingClientRect();
      if (r.bottom > 0) { var p = cardPath(kids[i]); if (p) return { a: p, o: Math.round(r.top) }; }
    }
    return { a: '', o: 0 };
  }
  function savePosition(card) {
    if (!grid() || busy) return;
    var an = anchorNow(card && card.nodeType === 1 ? card : null); if (!an) return;
    writeEntry({ a: an.a, o: an.o, y: Math.round(window.scrollY) });
  }
  function findCard(path) {
    var g = grid(); if (!g || !path) return null;
    for (var i = 0; i < g.children.length; i++) { if (cardPath(g.children[i]) === path) return g.children[i]; }
    return null;
  }
  function align(cm) {
    userMoved = false;
    function go() {
      if (userMoved) return;
      var c = findCard(cm.a);
      if (c) { var d = c.getBoundingClientRect().top - (cm.o || 0); if (Math.abs(d) > 2) scrollToY(window.scrollY + d); }
      else if (typeof cm.y === 'number') scrollToY(cm.y);
    }
    // chips, lazy rows and the shrinking sticky header keep moving the layout for a moment after a
    // swap; re-aim a few times, and stop the moment the shopper scrolls by hand
    go();
    requestAnimationFrame(function () { requestAnimationFrame(go); });
    [150, 350, 700, 1200, 1800].forEach(function (ms) { setTimeout(go, ms); });
  }

  /* ---- reading a fetched batch ---- */
  function parse(j, sid) {
    var dp = new DOMParser();
    var gd = dp.parseFromString(j[sid.gid] || '', 'text/html');
    var ng = gd.getElementById('product-grid');
    if (!ng) return null;
    var items = [].filter.call(ng.children, function (li) { return li.classList.contains('grid__item'); });
    var cc = [];
    if (sid.cid && j[sid.cid]) cc = [].slice.call(dp.parseFromString(j[sid.cid], 'text/html').querySelectorAll('.cvn-cc-data'));
    return { items: items, cc: cc, block: gd.querySelector('[data-cvn-more]') };
  }

  /* ---- putting cards on the page ---- */
  function prepReveal(nodes, animate) {
    var io = null;
    if (animate && 'IntersectionObserver' in window && typeof window.onIntersection === 'function') {
      try { io = new IntersectionObserver(window.onIntersection, { rootMargin: '0px 0px -50px 0px' }); } catch (e) { io = null; }
    }
    nodes.forEach(function (li) {
      if (!li.classList.contains('scroll-trigger')) return;
      if (io) li.classList.add('scroll-trigger--offscreen');
      else li.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
    });
    return io;
  }
  function append(g, items, ccNodes, animate) {
    var have = {};
    [].forEach.call(g.children, function (li) { var p = cardPath(li); if (p) have[p] = 1; });
    var keep = items.filter(function (li) { var p = cardPath(li); if (!p || have[p]) return false; have[p] = 1; return true; });
    var cw = ccWrap(), ccHtml = [];
    if (cw && ccNodes.length) {
      var seen = {};
      [].forEach.call(cw.querySelectorAll('.cvn-cc-data'), function (n) { seen[n.getAttribute('data-url')] = 1; });
      ccNodes.forEach(function (n) {
        var u = n.getAttribute('data-url');
        if (seen[u]) return; seen[u] = 1;
        ccHtml.push(n.outerHTML);
        cw.appendChild(document.importNode(n, true));
      });
    }
    var html = keep.map(function (li) { return li.outerHTML; });
    var nodes = keep.map(function (li) { return li.ownerDocument === document ? li : document.importNode(li, true); });
    var io = prepReveal(nodes, animate);
    var frag = document.createDocumentFragment();
    nodes.forEach(function (n) { frag.appendChild(n); });
    g.appendChild(frag);
    if (io) nodes.forEach(function (n) { if (n.classList.contains('scroll-trigger')) io.observe(n); });
    return { nodes: nodes, html: html, cc: ccHtml };
  }
  function updateBlock(blk, nb) {
    if (!blk) return;
    if (!nb) { blk.parentNode && blk.parentNode.removeChild(blk); return; }
    var c = blk.querySelector('[data-cvn-more-count]'), nc = nb.querySelector('[data-cvn-more-count]');
    if (c && nc && c.textContent !== nc.textContent) c.textContent = nc.textContent;
    var f = blk.querySelector('[data-cvn-more-fill]'), nf = nb.querySelector('[data-cvn-more-fill]');
    if (f && nf) f.style.width = nf.style.width;
    var b = blk.querySelector('[data-cvn-more-btn]'), nbtn = nb.querySelector('[data-cvn-more-btn]');
    if (b && nbtn) {
      b.setAttribute('href', cleanHref(nbtn.getAttribute('href')));
      b.classList.remove('is-loading'); b.removeAttribute('aria-busy');
    } else if (b) {
      b.parentNode.removeChild(b); // nothing left to show: no button
    }
    blk.setAttribute('data-page', nb.getAttribute('data-page') || '');
  }
  function placeBlockHtml(html) {
    var g = grid(); if (!g || !html) return;
    var nb = nodeFrom(html); if (!nb) return;
    var btn = nb.querySelector('[data-cvn-more-btn]');
    if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
    var old = moreBlock();
    if (old) old.parentNode.replaceChild(nb, old);
    else g.parentNode.insertBefore(nb, g.nextSibling);
  }
  function freezeAnchor(fn) {
    var de = document.documentElement, bd = document.body;
    var p1 = de.style.overflowAnchor, p2 = bd ? bd.style.overflowAnchor : '';
    de.style.overflowAnchor = 'none'; if (bd) bd.style.overflowAnchor = 'none';
    try { fn(); } finally {
      requestAnimationFrame(function () { requestAnimationFrame(function () {
        de.style.overflowAnchor = p1; if (bd) bd.style.overflowAnchor = p2;
      }); });
    }
  }

  /* ---- placeholder row ---- */
  function addPlaceholders(g) {
    var kids = g.children; if (!kids.length) return [];
    var first = kids[0], top = first.offsetTop, cols = 0;
    for (var i = 0; i < kids.length && kids[i].offsetTop === top; i++) cols++;
    var media = first.querySelector('.card__inner') || first;
    var mh = Math.round(media.getBoundingClientRect().height);
    var radius = getComputedStyle(media).borderRadius;
    var out = [];
    for (var j = 0; j < Math.max(1, cols); j++) {
      var li = document.createElement('li');
      li.className = 'grid__item cvn-more-ph';
      li.setAttribute('aria-hidden', 'true');
      li.innerHTML = '<div class="cvn-more-ph__media" style="height:' + mh + 'px;border-radius:' + radius + '"></div>' +
        '<div class="cvn-more-ph__line"></div><div class="cvn-more-ph__line"></div>';
      g.appendChild(li); out.push(li);
    }
    return out;
  }
  function drop(list) { list.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); }); }

  /* ---- Show more ---- */
  function load(btn, viaKeyboard) {
    if (busy) return;
    var g = grid(), blk = btn.closest('[data-cvn-more]');
    var href = cleanHref(btn.getAttribute('href'));
    var sid = g ? ids(g) : null;
    if (!g || !blk || !sid.gid || !window.fetch || !window.DOMParser) { window.location.assign(href); return; }
    busy = true;
    btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true');
    var ph = [], phT = setTimeout(function () { ph = addPlaceholders(g); }, PH_AFTER);
    function fail() {
      clearTimeout(phT); drop(ph); busy = false;
      btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy');
      // one quiet retry is the shopper's own second click; a second failure falls back to the page itself
      if (btn.getAttribute('data-cvn-more-failed')) window.location.assign(href);
      else btn.setAttribute('data-cvn-more-failed', '1');
    }
    fetch(sectionsUrl(href, sid.list), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
      .then(function (j) {
        clearTimeout(phT);
        if (grid() !== g) { busy = false; return; } // the page changed while this batch was on its way
        var res = parse(j, sid);
        if (!res) { fail(); return; }
        var added;
        freezeAnchor(function () {
          drop(ph);
          added = append(g, res.items, res.cc, true);
          updateBlock(blk, res.block);
        });
        busy = false;
        var cm = writeEntry({ hrefs: (entry() ? entry().hrefs : []).concat([href]) });
        if (cm) {
          var m = memFor(cm.id);
          m.items = m.items.concat(added.html);
          m.cc = m.cc.concat(added.cc);
          m.hrefs = cm.hrefs.slice();
          var live = moreBlock();
          m.block = live ? live.outerHTML : '';
        }
        if (viaKeyboard && added.nodes[0]) {
          var a = added.nodes[0].querySelector('a[href]');
          if (a) { try { a.focus({ preventScroll: true }); } catch (e) { a.focus(); } }
        }
        fire();
      })
      .catch(fail);
  }

  /* ---- coming back ---- */
  function restoreFromMemory(cm) {
    var g = grid(), m = cm && mem[cm.id];
    if (!g || !m || !m.items.length) return false;
    var have = {};
    [].forEach.call(g.children, function (li) { var p = cardPath(li); if (p) have[p] = 1; });
    var missing = m.items.some(function (h) { var n = nodeFrom(h); return n && !have[cardPath(n)]; });
    if (missing) {
      append(g, m.items.map(nodeFrom).filter(Boolean), m.cc.map(nodeFrom).filter(Boolean), false);
      placeBlockHtml(m.block);
      fire();
    }
    return true;
  }
  function rebuild(cm) {
    var g = grid(); if (!g) return;
    var sid = ids(g); if (!sid.gid) return;
    var hrefs = (cm.hrefs || []).slice(0, MAX_BATCHES);
    Promise.all(hrefs.map(function (h) {
      return fetch(sectionsUrl(h, sid.list), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (list) {
      if (grid() !== g) return;
      var m = memFor(cm.id), lastBlock = null, done = [];
      for (var i = 0; i < list.length; i++) {
        var res = list[i] && parse(list[i], sid);
        if (!res) break; // stop at the first gap so no batch is skipped
        var added = append(g, res.items, res.cc, false);
        m.items = m.items.concat(added.html); m.cc = m.cc.concat(added.cc);
        done.push(hrefs[i]); lastBlock = res.block;
      }
      if (lastBlock) { var blk = moreBlock(); if (blk) updateBlock(blk, lastBlock); else placeBlockHtml(lastBlock.outerHTML); }
      var live = moreBlock(); m.block = live ? live.outerHTML : '';
      m.hrefs = done.slice();
      writeEntry({ hrefs: done });
      if (done.length) fire();
      align(cm);
    });
  }
  function restore() {
    var cm = entry(), g = grid();
    if (!cm || !g) return;
    if (cm.hrefs && cm.hrefs.length) {
      if (restoreFromMemory(cm)) { align(cm); return; }
      rebuild(cm);
      return;
    }
    if (cm.a) align(cm);
  }

  /* ---- wiring (once per document; every page is a delegate) ---- */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-cvn-more-btn]') : null;
    if (!b) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    load(b, e.detail === 0);
  });
  // save the position BEFORE the soft-nav loader (document capture) empties the page
  window.addEventListener('click', function (e) {
    if (!grid()) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (a && !a.hasAttribute('data-cvn-more-btn')) savePosition(a.closest('#product-grid > li')); // the card they opened is the anchor
  }, true);
  var saveT = null;
  window.addEventListener('scroll', function () {
    if (!grid()) return;
    clearTimeout(saveT); saveT = setTimeout(savePosition, 200);
  }, { passive: true });
  window.addEventListener('pagehide', savePosition);
  ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(function (t) {
    window.addEventListener(t, function () { userMoved = true; }, { passive: true, capture: true });
  });
  document.addEventListener('cvn:navigated', restore);
  // the drawer re-rendered the first batch over a restored page (its own popstate handler)
  window.addEventListener('cvn:grid-updated', function () {
    var cm = entry();
    if (cm && cm.hrefs && cm.hrefs.length && restoreFromMemory(cm)) align(cm);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restore);
  else restore();
})();
